import {
  Add,
  Close,
  DirectionsBike,
  DirectionsRun,
  Edit,
  Info,
  Map as MapIcon,
  Remove,
  Save,
  Schedule,
  Speed,
  SwapHoriz,
  TrendingUp,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Slider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TrainingPlan, UserProfile, Workout, WorkoutStep } from '@shared/types';
import React, { lazy, Suspense, useState } from 'react';
import { useWorkoutTypes } from '../../contexts/WorkoutTypesContext';
import apiClient from '../../services/api';
import { formatDate, formatDistance } from '../../utils/dateUtils';
import WorkoutGraph from './WorkoutGraph';

// Lazy load RouteMap component to reduce initial bundle size
const RouteMap = lazy(() => import('./RouteMap'));

interface WorkoutTypeOption {
  id: string;
  name: string;
  type: string;
  sportType: 'running' | 'cycling';
}

interface WorkoutModalProps {
  workout: Workout | null;
  isOpen: boolean;
  onClose: () => void;
  onRemove: (workout: Workout) => void;
  isDeleting?: boolean;
  onUpdateDistance?: (workoutIndex: number, newDistance: number) => Promise<void>;
  onChangeWorkoutType?: (workoutIndex: number, newWorkoutTypeId: string) => Promise<void>;
  workoutTypes?: WorkoutTypeOption[];
  userProfile?: UserProfile;
  trainingPlan?: TrainingPlan;
}

const WorkoutModal: React.FC<WorkoutModalProps> = ({
  workout,
  isOpen,
  onClose,
  onRemove,
  isDeleting = false,
  onUpdateDistance,
  onChangeWorkoutType,
  workoutTypes = [],
  userProfile,
  trainingPlan,
}) => {
  const { getWorkoutTypeColor } = useWorkoutTypes();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isEditingDistance, setIsEditingDistance] = useState(false);
  const [editedDistance, setEditedDistance] = useState<number>(0);
  const [isUpdatingDistance, setIsUpdatingDistance] = useState(false);
  const [distanceError, setDistanceError] = useState<string>('');
  const [showRouteMap, setShowRouteMap] = useState(false);
  const [isRouteEditMode, setIsRouteEditMode] = useState(false);
  const [routeEditCallbacks, setRouteEditCallbacks] = useState<
    import('./RouteMap').RouteEditCallbacks | null
  >(null);
  const [isChangingWorkoutType, setIsChangingWorkoutType] = useState(false);
  const [selectedSportType, setSelectedSportType] = useState<'running' | 'cycling'>('running');
  const [selectedWorkoutTypeId, setSelectedWorkoutTypeId] = useState<string>('');
  const [isApplyingWorkoutChange, setIsApplyingWorkoutChange] = useState(false);
  const [workoutChangeError, setWorkoutChangeError] = useState<string>('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState<{
    changedWorkout: { before: Workout; after: Workout };
    affectedWorkouts: Array<{ before: Workout; after: Workout; reason: string }>;
    warnings: string[];
    isValid: boolean;
  } | null>(null);

  if (!workout) return null;

  // Check if this is a cycling workout
  const isCycling = workout.sportType === 'cycling';
  const isIndoor = workout.environment === 'indoor';

  // Check if this is a long run that can be edited
  const isLongRun = workout.name === 'Long Run';
  const canEditDistance = isLongRun && onUpdateDistance && userProfile && trainingPlan;

  // Check if workout type can be changed
  const canChangeWorkoutType = onChangeWorkoutType && workoutTypes.length > 0;

  // Filter workout types based on selected sport type
  const availableWorkoutTypes = workoutTypes.filter(wt => wt.sportType === selectedSportType);

  const handleStartEditDistance = () => {
    setEditedDistance(workout.total_distance || 0);
    setDistanceError('');
    setIsEditingDistance(true);
  };

  const handleCancelEditDistance = () => {
    setIsEditingDistance(false);
    setDistanceError('');
  };

  // Workout type change handlers
  const handleStartChangeWorkoutType = () => {
    setSelectedSportType(workout.sportType || 'running');
    setSelectedWorkoutTypeId('');
    setWorkoutChangeError('');
    setPreviewData(null);
    setIsChangingWorkoutType(true);
  };

  const handleCancelChangeWorkoutType = () => {
    setIsChangingWorkoutType(false);
    setSelectedWorkoutTypeId('');
    setWorkoutChangeError('');
    setPreviewData(null);
    setShowConfirmDialog(false);
  };

  const handlePreviewAndApply = async () => {
    if (!selectedWorkoutTypeId || !onChangeWorkoutType) return;

    setIsLoadingPreview(true);
    setWorkoutChangeError('');

    try {
      // First, get preview to check for warnings
      const response = await apiClient.previewWorkoutChange(workout.index, selectedWorkoutTypeId);

      if (!response.success || !response.data) {
        setWorkoutChangeError(response.error || 'Failed to preview workout change');
        return;
      }

      const preview = response.data.preview;
      setPreviewData(preview);

      if (!preview.isValid) {
        setWorkoutChangeError(preview.warnings.join(', '));
        return;
      }

      // If there are warnings or affected workouts, show confirmation dialog
      if (preview.warnings.length > 0 || preview.affectedWorkouts.length > 0) {
        setShowConfirmDialog(true);
      } else {
        // No warnings, apply directly
        await handleConfirmWorkoutChange();
      }
    } catch (err) {
      setWorkoutChangeError(
        err instanceof Error ? err.message : 'Failed to preview workout change'
      );
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleConfirmWorkoutChange = async () => {
    if (!selectedWorkoutTypeId || !onChangeWorkoutType) return;

    setIsApplyingWorkoutChange(true);
    setWorkoutChangeError('');
    setShowConfirmDialog(false);

    try {
      await onChangeWorkoutType(workout.index, selectedWorkoutTypeId);
      setIsChangingWorkoutType(false);
      setSelectedWorkoutTypeId('');
      setPreviewData(null);
    } catch (err) {
      setWorkoutChangeError(err instanceof Error ? err.message : 'Failed to change workout type');
    } finally {
      setIsApplyingWorkoutChange(false);
    }
  };

  const handleCancelConfirmDialog = () => {
    setShowConfirmDialog(false);
    // Keep the preview data so user can review and try again if needed
  };

  // Calculate min/max as 20% below/above current distance
  const minDistance = Math.max(1, Math.round((workout.total_distance || 10) * 0.8 * 2) / 2); // Round to nearest 0.5
  const maxDistance = Math.min(50, Math.round((workout.total_distance || 10) * 1.2 * 2) / 2); // Round to nearest 0.5

  const handleSaveDistance = async () => {
    // Validate distance
    if (editedDistance <= 0 || editedDistance > 50) {
      setDistanceError('Distance must be between 1 and 50 km');
      return;
    }

    if (editedDistance === workout.total_distance) {
      setIsEditingDistance(false);
      return;
    }

    setIsUpdatingDistance(true);
    try {
      await onUpdateDistance!(workout.index, editedDistance);
      setIsEditingDistance(false);
      setDistanceError('');
    } catch (error) {
      setDistanceError(error instanceof Error ? error.message : 'Failed to update distance');
    } finally {
      setIsUpdatingDistance(false);
    }
  };

  // Calculate average pace from workout (for running)
  const calculateAveragePace = (): string => {
    if (!workout.steps || workout.steps.length === 0) return 'N/A';

    // Calculate weighted average pace based on actual step paces and distances
    let totalDistance = 0;
    let totalTime = 0;

    const processStep = (step: WorkoutStep) => {
      if (step.repeat && step.steps) {
        // Handle repeated steps
        for (let i = 0; i < step.repeat; i++) {
          step.steps.forEach(processStep);
        }
      } else if (step.pace && step.duration) {
        // Calculate distance for this step: distance = duration / pace (in km)
        const stepDistance = step.duration / step.pace;
        totalDistance += stepDistance;
        totalTime += step.duration;
      } else if (step.distance && step.pace) {
        // Use provided distance
        totalDistance += step.distance / 1000; // Convert meters to km
        totalTime += (step.distance / 1000) * step.pace;
      }
    };

    workout.steps.forEach(processStep);

    if (totalDistance === 0 || totalTime === 0) {
      // Fallback to simple calculation if no steps have pace data
      if (workout.total_distance && workout.total_duration) {
        const paceSecondsPerKm = (workout.total_duration * 60) / workout.total_distance;
        const minutes = Math.floor(paceSecondsPerKm / 60);
        const seconds = Math.round(paceSecondsPerKm % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
      }
      return 'N/A';
    }

    // Calculate weighted average pace
    const avgPaceSecondsPerKm = totalTime / totalDistance;
    const minutes = Math.floor(avgPaceSecondsPerKm / 60);
    const seconds = Math.round(avgPaceSecondsPerKm % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
  };

  // Calculate average speed for cycling (km/h)
  const calculateAverageSpeed = (): string => {
    // Use step speed data if available
    if (workout.steps && workout.steps.length > 0) {
      let totalDistance = 0;
      let totalTime = 0;

      const processStep = (step: WorkoutStep) => {
        if (step.repeat && step.steps) {
          for (let i = 0; i < step.repeat; i++) {
            step.steps.forEach(processStep);
          }
        } else if (step.speed && step.duration) {
          // speed is in km/h, duration in seconds
          const hours = step.duration / 3600;
          const stepDistance = step.speed * hours;
          totalDistance += stepDistance;
          totalTime += step.duration;
        } else if (step.distance && step.speed) {
          totalDistance += step.distance / 1000;
          totalTime += (step.distance / 1000 / step.speed) * 3600;
        }
      };

      workout.steps.forEach(processStep);

      if (totalDistance > 0 && totalTime > 0) {
        const avgSpeedKmh = totalDistance / (totalTime / 3600);
        return `${avgSpeedKmh.toFixed(1)} km/h`;
      }
    }

    // Fallback: calculate from total distance and duration
    if (workout.total_distance && workout.total_duration) {
      const avgSpeedKmh = workout.total_distance / (workout.total_duration / 60);
      return `${avgSpeedKmh.toFixed(1)} km/h`;
    }

    return 'N/A';
  };

  const getIntensityColor = (
    intensity?: number
  ): 'info' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'default' => {
    switch (intensity) {
      case 1:
        return 'info';
      case 2:
        return 'success';
      case 3:
        return 'warning';
      case 4:
        return 'warning';
      case 5:
        return 'error';
      default:
        return 'default';
    }
  };

  // Get color for FTP% based on Coggan power zones
  const getFtpPercentColor = (
    ftpPercent: number
  ): 'info' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'default' => {
    if (ftpPercent < 75) return 'info'; // Recovery
    if (ftpPercent < 90) return 'success'; // Endurance
    if (ftpPercent < 95) return 'warning'; // Tempo
    if (ftpPercent <= 105) return 'warning'; // Threshold
    return 'error'; // VO2max+
  };

  // Get hex color for FTP% gradient backgrounds
  const getFtpPercentHexColor = (ftpPercent: number): string => {
    if (ftpPercent < 75) return '#4CAF50'; // Recovery - green
    if (ftpPercent < 90) return '#8BC34A'; // Endurance - light green
    if (ftpPercent < 95) return '#FFC107'; // Tempo - yellow
    if (ftpPercent <= 105) return '#FF9800'; // Threshold - orange
    return '#F44336'; // VO2max+ - red
  };

  const renderWorkoutStep = (step: WorkoutStep, index: number, isSubStep = false) => {
    // Helper function to format duration in mm:ss
    const formatDurationAsMinSec = (seconds: number): string => {
      const roundedSeconds = Math.round(seconds);
      const minutes = Math.floor(roundedSeconds / 60);
      const secs = roundedSeconds % 60;
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    // Helper function to format pace in mm:ss/km
    const formatPaceAsMinSec = (secondsPerKm: number): string => {
      const roundedSeconds = Math.round(secondsPerKm);
      const minutes = Math.floor(roundedSeconds / 60);
      const secs = roundedSeconds % 60;
      return `${minutes}:${secs.toString().padStart(2, '0')}/km`;
    };

    // Helper function to format distance
    const formatDistanceValue = (meters: number): string => {
      if (meters < 1000) {
        return `${Math.round(meters)} m`;
      } else {
        return `${(meters / 1000).toFixed(1)} km`;
      }
    };

    // Calculate missing parameters from available ones
    // We can have: duration + pace → distance, distance + pace → duration, or duration + distance → pace
    let calculatedDuration = step.duration;
    let calculatedDistance = step.distance;
    let calculatedPace = step.pace;

    // If we have duration and pace, calculate distance
    if (step.duration && step.pace && !step.distance) {
      // duration in seconds, pace in seconds per km
      // distance = (duration / pace) * 1000 (in meters)
      calculatedDistance = (step.duration / step.pace) * 1000;
    }
    // If we have distance and pace, calculate duration
    else if (step.distance && step.pace && !step.duration) {
      // distance in meters, pace in seconds per km
      // duration = (distance / 1000) * pace
      calculatedDuration = (step.distance / 1000) * step.pace;
    }
    // If we have duration and distance, calculate pace
    else if (step.duration && step.distance && !step.pace) {
      // duration in seconds, distance in meters
      // pace = duration / (distance / 1000)
      calculatedPace = step.duration / (step.distance / 1000);
    }

    const stepContent = (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
          <Box>
            <Typography variant="body1" component="div" gutterBottom>
              {step.text || `Step ${index + 1}`}
            </Typography>

            <Box>
              {/* Always show duration (calculated or provided) in mm:ss */}
              {calculatedDuration !== undefined && calculatedDuration > 0 && (
                <Typography variant="body2" color="text.secondary" component="span" display="block">
                  Duration: {formatDurationAsMinSec(calculatedDuration)}
                </Typography>
              )}

              {/* Always show distance (calculated or provided) */}
              {calculatedDistance !== undefined && calculatedDistance > 0 && (
                <Typography variant="body2" color="text.secondary" component="span" display="block">
                  Distance: {formatDistanceValue(calculatedDistance)}
                </Typography>
              )}

              {/* Always show pace (calculated or provided) in mm:ss/km */}
              {calculatedPace !== undefined && calculatedPace > 0 && (
                <Typography variant="body2" color="text.secondary" component="span" display="block">
                  Pace: {formatPaceAsMinSec(calculatedPace)}
                </Typography>
              )}

              {step.paceHigh && step.paceLow && (
                <Typography variant="body2" color="text.secondary" component="span" display="block">
                  Pace Range: {formatPaceAsMinSec(step.paceHigh)} -{' '}
                  {formatPaceAsMinSec(step.paceLow)}
                </Typography>
              )}
              {step.repeat && step.repeat > 1 && (
                <Typography variant="body2" color="text.secondary" component="span" display="block">
                  Repeat: {step.repeat} times
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
            {/* Show zone-based intensity chip */}
            {(step.zone ||
              step.ramp?.startZone ||
              step.ftpPercent ||
              step.ramp?.startFtpPercent !== undefined) && (
              <Chip
                label={
                  step.ftpPercent
                    ? `${typeof step.ftpPercent === 'number' ? step.ftpPercent : ((step.ftpPercent.min + step.ftpPercent.max) / 2).toFixed(0)}% FTP`
                    : step.zone
                      ? `Zone ${step.zone}`
                      : step.ramp?.startZone
                        ? `Z${step.ramp.startZone}→${step.ramp.endZone}`
                        : ''
                }
                size="small"
                color={
                  step.ftpPercent
                    ? getFtpPercentColor(
                        typeof step.ftpPercent === 'number'
                          ? step.ftpPercent
                          : (step.ftpPercent.min + step.ftpPercent.max) / 2
                      )
                    : step.ramp?.startFtpPercent
                      ? getFtpPercentColor(step.ramp.startFtpPercent)
                      : getIntensityColor(step.zone || step.ramp?.startZone)
                }
              />
            )}
            {/* FTP% ramp (indoor cycling) */}
            {step.ramp?.startFtpPercent !== undefined && step.ramp?.endFtpPercent !== undefined ? (
              <Chip
                label={`${step.ramp.startFtpPercent}% → ${step.ramp.endFtpPercent}% FTP`}
                size="small"
                variant="outlined"
                sx={{
                  background: `linear-gradient(90deg, 
                    ${getFtpPercentHexColor(step.ramp.startFtpPercent)} 0%, 
                    ${getFtpPercentHexColor(step.ramp.endFtpPercent)} 100%)`,
                  color: 'white',
                  fontWeight: 'bold',
                  borderColor: 'transparent',
                }}
              />
            ) : /* Zone ramp (running) */ step.ramp?.startZone !== undefined &&
              step.ramp?.endZone !== undefined ? (
              <Chip
                label={`Z${step.ramp.startZone} → Z${step.ramp.endZone}`}
                size="small"
                variant="outlined"
                sx={{
                  background: `linear-gradient(90deg, 
                    ${step.ramp.startZone === 1 ? '#4CAF50' : step.ramp.startZone === 2 ? '#8BC34A' : step.ramp.startZone === 3 ? '#FFC107' : step.ramp.startZone === 4 ? '#FF9800' : '#F44336'} 0%, 
                    ${step.ramp.endZone === 1 ? '#4CAF50' : step.ramp.endZone === 2 ? '#8BC34A' : step.ramp.endZone === 3 ? '#FFC107' : step.ramp.endZone === 4 ? '#FF9800' : '#F44336'} 100%)`,
                  color: 'white',
                  fontWeight: 'bold',
                  borderColor: 'transparent',
                }}
              />
            ) : /* Single FTP% value (indoor cycling) */ step.ftpPercent !== undefined ? (
              <Chip
                label={`${typeof step.ftpPercent === 'number' ? step.ftpPercent : `${step.ftpPercent.min}-${step.ftpPercent.max}`}% FTP`}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: getFtpPercentHexColor(
                    typeof step.ftpPercent === 'number'
                      ? step.ftpPercent
                      : (step.ftpPercent.min + step.ftpPercent.max) / 2
                  ),
                  color: 'white',
                  fontWeight: 'bold',
                  borderColor: 'transparent',
                }}
              />
            ) : /* Zone (running) */ step.zone ? (
              <Chip label={`Zone ${step.zone}`} size="small" variant="outlined" />
            ) : null}
          </Box>
        </Box>

        {step.steps && step.steps.length > 0 && (
          <Box ml={2} mt={1} width="100%">
            <Box component="div" ml={1}>
              {step.steps.map((subStep, subIndex) => (
                <Box
                  key={subIndex}
                  py={1}
                  px={2}
                  sx={{ borderLeft: '2px solid', borderColor: theme.palette.divider }}
                >
                  {renderWorkoutStep(subStep, subIndex, true)}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </>
    );

    // If it's a sub-step, don't wrap in ListItem to avoid nesting <li> elements
    if (isSubStep) {
      return stepContent;
    }

    // Only top-level steps use ListItem
    return (
      <ListItem key={index} divider>
        {stepContent}
      </ListItem>
    );
  };

  const handleRemoveConfirm = () => {
    onRemove(workout);
    setShowRemoveConfirm(false);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isRouteEditMode ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isRouteEditMode || isMobile}
      PaperProps={{
        sx: {
          minHeight: isMobile ? '100vh' : '60vh',

          backgroundColor: alpha(theme.palette.background.paper, 0.85),
          border: `3px solid ${getWorkoutTypeColor(workout.name).primary}`,
          borderRadius: isRouteEditMode ? 0 : 3,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.8)'
              : '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          backgroundColor: alpha(theme.palette.background.default, 0.3),
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: alpha(getWorkoutTypeColor(workout.name).primary, 0.08),
          borderBottom: `2px solid ${alpha(getWorkoutTypeColor(workout.name).primary, 0.2)}`,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Typography
            variant="h5"
            sx={{
              color: getWorkoutTypeColor(workout.name).primary,
              fontWeight: 'bold',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            {isRouteEditMode
              ? 'Edit Route'
              : `${workout.name} - ${formatDate(workout.training_date)}`}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {isRouteEditMode && routeEditCallbacks && (
              <>
                <Button
                  variant="contained"
                  onClick={routeEditCallbacks.onSaveChanges}
                  disabled={routeEditCallbacks.isLoading}
                  size="small"
                  aria-label="Save route changes"
                >
                  {routeEditCallbacks.isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={routeEditCallbacks.onCancelEdit}
                  disabled={routeEditCallbacks.isLoading}
                  size="small"
                  aria-label="Cancel route editing"
                >
                  Cancel
                </Button>
              </>
            )}
            <IconButton
              onClick={isRouteEditMode ? undefined : onClose}
              disabled={isRouteEditMode}
              aria-label="Close workout modal"
              sx={{
                color: getWorkoutTypeColor(workout.name).primary,
                '&:hover': {
                  backgroundColor: alpha(getWorkoutTypeColor(workout.name).primary, 0.1),
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          overflow: isRouteEditMode ? 'hidden' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          p: isRouteEditMode ? 1 : 3,
        }}
      >
        {/* Workout Overview */}
        {!isRouteEditMode && (
          <Box mb={3}>
            {/* Workout Type Change UI */}
            {canChangeWorkoutType && !isChangingWorkoutType && (
              <Box mb={2}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleStartChangeWorkoutType}
                  startIcon={<SwapHoriz />}
                  aria-label="Change workout type"
                  sx={{ mr: 1 }}
                >
                  Change Type
                </Button>
              </Box>
            )}

            {isChangingWorkoutType && (
              <Box mb={3} p={2} sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <SwapHoriz color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Change Workout Type
                  </Typography>
                </Box>

                {workoutChangeError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {workoutChangeError}
                  </Alert>
                )}

                <Box display="flex" gap={2} mb={2}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="sport-type-select-label">Sport</InputLabel>
                    <Select
                      labelId="sport-type-select-label"
                      value={selectedSportType}
                      label="Sport"
                      onChange={e => {
                        setSelectedSportType(e.target.value as 'running' | 'cycling');
                        setSelectedWorkoutTypeId(''); // Reset workout type when sport changes
                      }}
                      disabled={isApplyingWorkoutChange}
                      aria-label="Select sport type"
                    >
                      <MenuItem value="running">
                        <Box display="flex" alignItems="center" gap={1}>
                          <DirectionsRun fontSize="small" />
                          Running
                        </Box>
                      </MenuItem>
                      <MenuItem value="cycling">
                        <Box display="flex" alignItems="center" gap={1}>
                          <DirectionsBike fontSize="small" />
                          Cycling
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel id="workout-type-select-label">Workout Type</InputLabel>
                    <Select
                      labelId="workout-type-select-label"
                      value={selectedWorkoutTypeId}
                      label="Workout Type"
                      onChange={e => setSelectedWorkoutTypeId(e.target.value)}
                      disabled={isApplyingWorkoutChange}
                      aria-label="Select new workout type"
                    >
                      {availableWorkoutTypes.map(wt => (
                        <MenuItem
                          key={wt.id}
                          value={wt.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            '& .workout-type-badge': {
                              fontSize: '0.75rem',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: wt.type === 'hard' ? 'error.light' : 'success.light',
                              color:
                                wt.type === 'hard' ? 'error.contrastText' : 'success.contrastText',
                            },
                          }}
                        >
                          <span>{wt.name}</span>
                          <span className="workout-type-badge">{wt.type}</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handlePreviewAndApply}
                    disabled={!selectedWorkoutTypeId || isApplyingWorkoutChange || isLoadingPreview}
                    startIcon={
                      isLoadingPreview || isApplyingWorkoutChange ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Save />
                      )
                    }
                    aria-label="Apply workout type change"
                  >
                    {isLoadingPreview
                      ? 'Checking...'
                      : isApplyingWorkoutChange
                        ? 'Applying...'
                        : 'Apply Change'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelChangeWorkoutType}
                    disabled={isApplyingWorkoutChange}
                    aria-label="Cancel workout type change"
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {!isCycling && isEditingDistance ? (
              // Full-width distance editor when editing
              <Box mb={3} p={2} sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <DirectionsRun color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Adjust Distance
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} mb={1} justifyContent="center">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newValue = Math.max(minDistance, editedDistance - 0.5);
                      setEditedDistance(newValue);
                      setDistanceError('');
                    }}
                    disabled={isUpdatingDistance || editedDistance <= minDistance}
                    aria-label="Decrease workout distance"
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <Remove fontSize="small" />
                  </IconButton>

                  <Typography
                    variant="h5"
                    sx={{ minWidth: '100px', textAlign: 'center', fontWeight: 'bold' }}
                  >
                    {editedDistance.toFixed(1)} km
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => {
                      const newValue = Math.min(maxDistance, editedDistance + 0.5);
                      setEditedDistance(newValue);
                      setDistanceError('');
                    }}
                    disabled={isUpdatingDistance || editedDistance >= maxDistance}
                    aria-label="Increase workout distance"
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>

                <Slider
                  value={editedDistance}
                  onChange={(_, value) => {
                    setEditedDistance(value as number);
                    setDistanceError('');
                  }}
                  min={minDistance}
                  max={maxDistance}
                  step={0.5}
                  marks={[
                    { value: minDistance, label: `${minDistance}km` },
                    {
                      value: workout.total_distance || 10,
                      label: `${workout.total_distance || 10}km`,
                    },
                    { value: maxDistance, label: `${maxDistance}km` },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value}km`}
                  disabled={isUpdatingDistance}
                  aria-label="Adjust workout distance slider"
                  sx={{ mt: 1, mb: 2 }}
                />

                {distanceError && (
                  <Typography variant="caption" color="error" display="block" mb={1}>
                    {distanceError}
                  </Typography>
                )}

                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    onClick={handleCancelEditDistance}
                    disabled={isUpdatingDistance}
                    aria-label="Cancel distance edit"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveDistance}
                    disabled={isUpdatingDistance}
                    startIcon={isUpdatingDistance ? <CircularProgress size={16} /> : <Save />}
                    aria-label="Save distance changes"
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : null}

            {/* Running Stats */}
            {!isCycling && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 6, md: 3 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    {isCycling ? (
                      <DirectionsBike color="primary" sx={{ mr: 1 }} />
                    ) : (
                      <DirectionsRun color="primary" sx={{ mr: 1 }} />
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Distance
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="h6">
                          {formatDistance(workout.total_distance || 0)}
                        </Typography>
                        {canEditDistance && !isEditingDistance && (
                          <IconButton
                            size="small"
                            onClick={handleStartEditDistance}
                            sx={{ p: 0.5 }}
                            aria-label="Edit workout distance"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 3 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Schedule color="primary" sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="h6">{workout.total_duration} min</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 3 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Speed color="primary" sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {isCycling ? 'Avg Speed' : 'Avg Pace'}
                      </Typography>
                      <Typography variant="h6">
                        {isCycling ? calculateAverageSpeed() : calculateAveragePace()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 3 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingUp color="primary" sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        TSS
                      </Typography>
                      <Typography variant="h6">{Math.round(workout.TSS || 0)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* Cycling Stats */}
            {isCycling && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 6, md: isIndoor ? 6 : 4 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <DirectionsBike color="primary" sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="h6">{workout.total_duration} min</Typography>
                    </Box>
                  </Box>
                </Grid>

                {!isIndoor && (
                  <Grid size={{ xs: 6, sm: 6, md: 4 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Speed color="primary" sx={{ mr: 1 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Avg Speed
                        </Typography>
                        <Typography variant="h6">{calculateAverageSpeed()}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 6, sm: 6, md: isIndoor ? 6 : 4 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TrendingUp color="primary" sx={{ mr: 1 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        TSS
                      </Typography>
                      <Typography variant="h6">{Math.round(workout.TSS || 0)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* AI Coach Notes */}
            {workout.note && (
              <Box
                mt={2}
                p={2}
                sx={{
                  bgcolor: alpha(getWorkoutTypeColor(workout.name).primary, 0.08),
                  borderRadius: 1,
                  borderLeft: `4px solid ${getWorkoutTypeColor(workout.name).primary}`,
                }}
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <Info color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="primary">
                    AI Coach Notes
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {workout.note}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {!isRouteEditMode && <Divider sx={{ mb: 1 }} />}
        {!isRouteEditMode && (
          <Box mb={2}>
            <WorkoutGraph workout={workout} userFTP={userProfile?.FTP} />
          </Box>
        )}
        {!isRouteEditMode && <Divider sx={{ mb: 2 }} />}

        {/* Route Generation Section */}
        {!isRouteEditMode && !isCycling && (workout.total_distance || 0) > 0 && !showRouteMap && (
          <Box mb={2}>
            <Button
              variant="outlined"
              startIcon={<MapIcon />}
              onClick={() => setShowRouteMap(true)}
              fullWidth
              aria-label="Generate route suggestion"
            >
              Generate Route Suggestion
            </Button>
          </Box>
        )}

        {showRouteMap && !isCycling && (
          <Box mb={3}>
            <Suspense
              fallback={
                <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress />
                </Box>
              }
            >
              <RouteMap
                distance={workout.total_distance || 0}
                onClose={() => setShowRouteMap(false)}
                onEditModeChange={setIsRouteEditMode}
                onEditCallbacks={setRouteEditCallbacks}
              />
            </Suspense>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* Workout Steps */}
        {!isRouteEditMode && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Workout Details
            </Typography>
            {workout.steps && workout.steps.length > 0 ? (
              <List>{workout.steps.map((step, index) => renderWorkoutStep(step, index))}</List>
            ) : (
              <Alert severity="info">No detailed workout steps available.</Alert>
            )}
          </Box>
        )}

        {/* Remove Confirmation Overlay */}
        {showRemoveConfirm && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 10,
              borderRadius: 1,
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 3,
                maxWidth: 400,
                textAlign: 'center',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" gutterBottom color="warning.main">
                Remove Workout?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Are you sure you want to remove this workout from your training plan?
              </Typography>
              <Box display="flex" gap={2} justifyContent="center">
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleRemoveConfirm}
                  disabled={isDeleting}
                  startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
                  aria-label="Confirm remove workout"
                >
                  {isDeleting ? 'Removing...' : 'Yes, Remove'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowRemoveConfirm(false)}
                  disabled={isDeleting}
                  aria-label="Cancel remove workout"
                >
                  Cancel
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      {!isRouteEditMode && (
        <DialogActions
          sx={{
            backgroundColor: alpha(getWorkoutTypeColor(workout.name).primary, 0.04),
            borderTop: `1px solid ${alpha(getWorkoutTypeColor(workout.name).primary, 0.2)}`,
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
          }}
        >
          <Button
            color="error"
            onClick={() => setShowRemoveConfirm(true)}
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : undefined}
            aria-label="Remove workout from training plan"
          >
            {isDeleting ? 'Removing...' : 'Remove Workout'}
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            disabled={isDeleting}
            aria-label="Close workout modal"
            sx={{
              backgroundColor: getWorkoutTypeColor(workout.name).primary,
              '&:hover': {
                backgroundColor: getWorkoutTypeColor(workout.name).primary,
                filter: 'brightness(0.9)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      )}

      {/* Workout Change Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelConfirmDialog}
        maxWidth="sm"
        fullWidth
        aria-label="Confirm workout type change"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6">Confirm Workout Change</Typography>
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <Box>
              {/* Main Change */}
              <Box mb={2} p={2} sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Changing Workout:
                </Typography>
                {(() => {
                  const workoutDate = new Date(previewData.changedWorkout.before.training_date);
                  const dayName = workoutDate.toLocaleDateString('en-US', { weekday: 'short' });
                  const dateStr = `${workoutDate.getMonth() + 1}/${workoutDate.getDate()}`;
                  const formattedDate = `${dayName} ${dateStr}`;
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ minWidth: 70, fontWeight: 500 }}>
                        {formattedDate}
                      </Typography>
                      <Chip
                        label={previewData.changedWorkout.before.name}
                        size="small"
                        sx={{
                          bgcolor:
                            previewData.changedWorkout.before.type === 'hard'
                              ? 'error.light'
                              : 'success.light',
                          color:
                            previewData.changedWorkout.before.type === 'hard'
                              ? 'error.contrastText'
                              : 'success.contrastText',
                        }}
                      />
                      <Typography color="text.secondary">→</Typography>
                      <Chip
                        label={previewData.changedWorkout.after.name}
                        size="small"
                        sx={{
                          bgcolor:
                            previewData.changedWorkout.after.type === 'hard'
                              ? 'error.light'
                              : 'success.light',
                          color:
                            previewData.changedWorkout.after.type === 'hard'
                              ? 'error.contrastText'
                              : 'success.contrastText',
                        }}
                      />
                    </Box>
                  );
                })()}
              </Box>

              {/* Warnings */}
              {previewData.warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
                  <Typography variant="subtitle2" gutterBottom>
                    Warnings:
                  </Typography>
                  <List dense disablePadding>
                    {previewData.warnings.map((warning, idx) => (
                      <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                        <Typography variant="body2">{warning}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}

              {/* Affected Workouts */}
              {previewData.affectedWorkouts.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Other workouts that will be adjusted:
                  </Typography>
                  <List dense disablePadding>
                    {previewData.affectedWorkouts.map((affected, idx) => {
                      // Format the date as "Mon 4/1"
                      const workoutDate = new Date(affected.before.training_date);
                      const dayName = workoutDate.toLocaleDateString('en-US', { weekday: 'short' });
                      const dateStr = `${workoutDate.getMonth() + 1}/${workoutDate.getDate()}`;
                      const formattedDate = `${dayName} ${dateStr}`;

                      return (
                        <ListItem
                          key={idx}
                          disableGutters
                          sx={{
                            py: 1,
                            px: 2,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            mb: 1,
                            display: 'block',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography variant="body2" sx={{ minWidth: 70, fontWeight: 500 }}>
                              {formattedDate}
                            </Typography>
                            <Chip
                              label={affected.before.name}
                              size="small"
                              sx={{
                                bgcolor:
                                  affected.before.type === 'hard' ? 'error.light' : 'success.light',
                                color:
                                  affected.before.type === 'hard'
                                    ? 'error.contrastText'
                                    : 'success.contrastText',
                              }}
                            />
                            <Typography color="text.secondary">→</Typography>
                            <Chip
                              label={affected.after.name}
                              size="small"
                              sx={{
                                bgcolor:
                                  affected.after.type === 'hard' ? 'error.light' : 'success.light',
                                color:
                                  affected.after.type === 'hard'
                                    ? 'error.contrastText'
                                    : 'success.contrastText',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ pl: '70px' }}>
                            {affected.reason}
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                Do you want to proceed with this change?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleCancelConfirmDialog}
            variant="outlined"
            disabled={isApplyingWorkoutChange}
            aria-label="Cancel workout change"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmWorkoutChange}
            variant="contained"
            color="warning"
            disabled={isApplyingWorkoutChange}
            startIcon={isApplyingWorkoutChange ? <CircularProgress size={16} /> : undefined}
            aria-label="Confirm workout type change"
          >
            {isApplyingWorkoutChange ? 'Applying...' : 'Confirm Change'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default WorkoutModal;
